import React, { useState } from 'react';
import { Radio, Zap } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData, categoryColors } from '../../../data/eventsData';

const getImpactStyle = (impact: string): string => {
  const styles: Record<string, string> = {
    'CRITICAL': 'bg-data-red text-white',
    'HIGH': 'bg-data-green text-black',
    'MED': 'bg-data-amber text-black',
    'LOW': 'bg-gray-600 text-white',
  };
  return styles[impact] || 'bg-gray-600 text-white';
};

const getRelativeTime = (dateStr: string): string => {
  const eventDate = new Date(dateStr.split(' ')[0]);
  const today = new Date('2024-12-12');
  const diffDays = Math.floor((today.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'TODAY';
  if (diffDays === 1) return '-1D';
  return `-${diffDays}D`;
};

interface LiveFeedPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const LiveFeedPanel: React.FC<LiveFeedPanelProps> = ({ isMinimized, onMinimize }) => {
  const [filter, setFilter] = useState<string>('ALL');
  const { liveFeed } = eventsData;

  const categories = ['ALL', 'RATING', 'FDI', 'POLICY', 'ECONOMIC', 'TRADE'];

  const filteredFeed = filter === 'ALL'
    ? liveFeed
    : liveFeed.filter(e => e.category === filter);

  const highImpactCount = liveFeed.filter(e => e.impact === 'HIGH' || e.impact === 'CRITICAL').length;
  const todayCount = liveFeed.filter(e => getRelativeTime(e.time) === 'TODAY').length;

  return (
    <Panel title="LIVE FEED" subtitle="UPDATES" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{liveFeed.length}</div>
            <div className="text-xs text-gray-500">Total Events</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-data-green/30">
            <div className="text-2xl font-mono font-bold text-data-green">{todayCount}</div>
            <div className="text-xs text-gray-500">Today</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-amber">{highImpactCount}</div>
            <div className="text-xs text-gray-500">High Impact</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1.5 mr-3">
            <span className="w-2.5 h-2.5 bg-data-green rounded-full animate-pulse" />
            <span className="text-xs text-data-green font-semibold">LIVE</span>
          </div>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-2.5 py-1.5 text-xs rounded transition-all ${
                filter === cat
                  ? 'bg-data-green/20 text-data-green border border-data-green/30'
                  : 'bg-terminal-bg/40 text-gray-500 hover:text-white border border-transparent'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Event Feed */}
        <div className="flex-1 overflow-auto custom-scrollbar space-y-2">
          {filteredFeed.slice(0, 8).map((event, idx) => {
            const time = event.time.split(' ')[1];
            const relTime = getRelativeTime(event.time);

            return (
              <div
                key={idx}
                className={`bg-terminal-bg/40 rounded-lg p-3 border ${
                  relTime === 'TODAY' ? 'border-data-green/30' : 'border-terminal-border/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-mono font-bold ${relTime === 'TODAY' ? 'text-data-green' : 'text-gray-400'}`}>
                      {relTime}
                    </span>
                    <span className="text-xs text-gray-500">{time}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${getImpactStyle(event.impact)}`}>
                    {event.impact}
                  </span>
                </div>
                <div className="text-sm text-white mb-1.5">{event.event}</div>
                <span
                  className="text-xs font-medium"
                  style={{ color: categoryColors[event.category] }}
                >
                  {event.category}
                </span>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-3 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default LiveFeedPanel;
